import qs from 'qs';
import { get } from 'lodash';
import { removeEmptyAttrs } from '@/common/helpers';
import appFeatures from '@/appFeatures.json';
import http from '../utils/http';
import features from '../utils/features';
import appAbility from '../utils/appAbility';

// this is a partial store members that will be called from each store that
// need the api data table search/pagination functions
// this is the store template for any view that uses the ds search API which
// returns data and page information
export const defaultState = {
    items: [],
    tableFields: [],
    computedFields: [],
    filterFields: [],
    exportFields: {},
    exportFileName: '',
    requestQuery: {},
    responseQuery: {
        totalPages: 0,
        totalElements: 0
    },
    filterQuery: {},
    zebraStripping: false,
    fieldsConfig: {},
    actionsConfig: {},
    buttonsConfig: {},
    queryConfig: {},
    filtersConfig: {},
    optionsConfig: {}
};

export const mutations = {
    setItems(state, value) {
        if (Array.isArray(value)) {
            state.items = value;
        } else {
            state.items = [];
        }
    },
    setTableFields(state, value) {
        if (Array.isArray(value)) {
            state.tableFields = value;
        } else {
            state.tableFields = [];
        }
    },
    setComputedFields(state, value) {
        if (Array.isArray(value)) {
            state.computedFields = value;
        } else {
            state.computedFiels = [];
        }
    },
    setFilterFields(state, value) {
        if (Array.isArray(value)) {
            state.filterFields = value;
        } else {
            state.filterFields = [];
        }
    },
    setExportFields(state, value) {
        if (value) {
            state.exportFields = value;
        } else {
            state.tableFields = {};
        }
    },
    setRequestQuery(state, value) {
        if (value) {
            state.requestQuery = value;
        } else {
            state.requestQuery = {};
        }
    },
    setResponseQuery(state, value) {
        if (value) {
            state.responseQuery = value;
        } else {
            state.responseQuery = {};
        }
    },
    setFilterQuery(state, value) {
        if (value) {
            state.filterQuery = value;
        } else {
            state.filterQuery = {};
        }
    },
    setZebraStripping(state, value) {
        if (typeof value === 'boolean') {
            state.zebraStripping = value;
        } else {
            state.zebraStripping = false;
        }
    },
    setFieldsConfig(state, value) {
        if (value) {
            state.fieldsConfig = value;
        } else {
            state.fieldsConfig = {};
        }
    },
    setActionsConfig(state, value) {
        if (value) {
            state.actionsConfig = value;
        } else {
            state.actionsConfig = {};
        }
    },
    setButtonsConfig(state, value) {
        if (value) {
            state.buttonsConfig = value;
        } else {
            state.buttonsConfig = {};
        }
    },
    setQueryConfig(state, value) {
        if (value) {
            state.queryConfig = value;
        } else {
            state.queryConfig = {};
        }
    },
    setExportFileName(state, value) {
        if (typeof value === 'string') {
            state.exportFileName = value;
        } else {
            state.exportFileName = '';
        }
    },
    setOptionsConfig(state, value) {
        if (value) {
            state.optionsConfig = value;
        } else {
            state.optionsConfig = {};
        }
    },
    setFiltersConfig(state, value) {
        if (value) {
            state.filtersConfig = value;
        } else {
            state.filtersConfig = {};
        }
    }
};

export const actions = {
    fetchItems(
        { commit, state },
        { searchParams, filterParams, notifyConfig, saveParams, isDsSearch }
    ) {
        console.log('calling fetchItems');
        console.time(`fetchItems-${state.entityName}`);
        const subUrl = isDsSearch === false ? '' : 'search';
        const cleanFilterParams = removeEmptyAttrs(filterParams);
        // need to clear the sorting if q is provided and rely on the
        // default DS search sorting.
        if (!isDsSearch && searchParams && searchParams.q) {
            searchParams.sort = '';
            searchParams.desc = '';
        } else if (!searchParams.sort) {
            // need to restore the default sort
            searchParams.sort = state.queryConfig.sort;
            searchParams.desc = state.queryConfig.desc;
        }
        if (saveParams) {
            commit('setRequestQuery', {
                ...searchParams
            });
            commit('setFilterQuery', cleanFilterParams);
        }
        let queryString = features.parseSortDirection(
            features.parseSortField(searchParams, state.fieldsConfig)
        );
        // need to build the final query string
        const queryStringUrl = qs.stringify(
            { ...queryString, ...cleanFilterParams },
            { arrayFormat: 'comma' }
        );
        console.timeEnd(`fetchItems-${state.entityName}`);
        return http.get(`${state.baseUrl}${subUrl}?${queryStringUrl}`, {
            ...notifyConfig
        });
    },
    setupView({ dispatch, state, rootState }) {
        const viewName = state.entityName;
        const featuresConfig = appFeatures;
        console.log('calling setupView', viewName);
        return dispatch('loadViewConfig', {
            viewName,
            tableName: state.tableName,
            featuresConfig
        })
            .then(() => {
                return dispatch('buildViewAbilities', {
                    viewName,
                    tableName: state.tableName,
                    fieldsConfig: state.fieldsConfig,
                    actionsConfig: state.actionsConfig,
                    buttonsConfig: state.buttonsConfig,
                    userPermissions: rootState.authUser.userPermissions
                });
            })
            .then(() => {
                if (state.tableName) {
                    return dispatch('buildDataTable', {
                        viewName,
                        tableName: state.tableName,
                        fieldsConfig: state.fieldsConfig,
                        abilities: appAbility
                    });
                }
            });
    },
    buildDataTable(
        { commit, state },
        { viewName, tableName, fieldsConfig, abilities }
    ) {
        // will build the data table from appFeatures config file
        console.log('calling buildDataTable:', tableName);

        const tableFields = features.buildTableFields(
            viewName,
            tableName,
            fieldsConfig,
            abilities
        );
        // need to insert extra table column for row action buttons
        tableFields.push({
            text: '',
            value: 'rowActions',
            sortable: false
        });

        const computedFields = features.buildComputedFields(state.fieldsConfig);
        const exportFields = features.buildExportFields(state.fieldsConfig);
        const filterFields = features.buildFilterFields(state.fieldsConfig);

        commit('setTableFields', tableFields);
        commit('setComputedFields', computedFields);
        commit('setExportFields', exportFields);
        commit('setFilterFields', filterFields);
    },
    buildViewAbilities(
        { commit },
        {
            viewName,
            tableName,
            fieldsConfig,
            actionsConfig,
            buttonsConfig,
            userPermissions
        }
    ) {
        // will add the datatable and buttons abilities based on user permissions
        console.log('calling buildViewAbilities', viewName);
        // ========= data table rules ==============
        if (tableName) {
            const tableRules = features.buildDataTableAbilities(
                viewName,
                tableName,
                fieldsConfig,
                actionsConfig,
                userPermissions
            );
            if (tableRules && tableRules.length) {
                commit('updateAppAbilities', tableRules, { root: true });
            }
        }
        // ========= view buttons rules ==============
        const buttonRules = features.buildViewButtonsAbilities(
            viewName,
            buttonsConfig,
            actionsConfig,
            userPermissions
        );
        if (buttonRules && buttonRules.length) {
            commit('updateAppAbilities', buttonRules, { root: true });
        }
    },
    loadViewConfig(
        { commit, dispatch },
        { viewName, tableName, featuresConfig }
    ) {
        console.log('calling loadViewConfig', viewName);
        // will load the view configuration from the appFeatures json
        const defaultQueryConfig = {
            number: 1,
            size: 10,
            sort: '',
            q: '',
            desc: false
        };

        const actionsConfig =
            get(featuresConfig, `views.${viewName}.actions`) || {};
        const buttonsConfig =
            get(featuresConfig, `views.${viewName}.displaySettings.buttons`) ||
            {};
        const optionsConfig =
            get(featuresConfig, `views.${viewName}.displaySettings.options`) ||
            {};
        commit('setActionsConfig', actionsConfig);
        commit('setButtonsConfig', buttonsConfig);
        commit('setOptionsConfig', optionsConfig);

        // table related settings
        if (tableName) {
            const fieldsConfig =
                get(
                    featuresConfig,
                    `views.${viewName}.displaySettings.${tableName}.fields`
                ) || {};
            const zebraStripping =
                get(
                    featuresConfig,
                    `views.${viewName}.displaySettings.${tableName}.zebraStripping`
                ) || false;
            const queryConfig =
                get(
                    featuresConfig,
                    `views.${viewName}.displaySettings.${tableName}.query`
                ) || {};
            const exportFileName =
                get(
                    featuresConfig,
                    `views.${viewName}.displaySettings.${tableName}.exportFileName`
                ) || `data-export-${viewName}.csv`;
            const filtersConfig =
                get(
                    featuresConfig,
                    `views.${viewName}.displaySettings.${tableName}.filters`
                ) || {};
            commit('setFieldsConfig', fieldsConfig);
            commit('setQueryConfig', { ...defaultQueryConfig, ...queryConfig });
            commit('setZebraStripping', zebraStripping);
            commit('setExportFileName', exportFileName);
            commit('setFiltersConfig', filtersConfig);
            dispatch('resetRequestQuery');
        }
    },
    resetRequestQuery({ commit, state }) {
        // will reset the requestQuery to it's initial state
        // need to be called on view creation to reset the query parameters
        const defaultQueryConfig = {
            number: 1,
            size: 10,
            sort: '',
            q: '',
            desc: false
        };
        commit('setRequestQuery', {
            ...defaultQueryConfig,
            ...state.queryConfig
        });
        commit('setFilterQuery', {
            ...state.filtersConfig
        });
    }
};
